import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import type React from "react";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";

import type { OfferedAnswerDto, RankingAnswerItem } from "@/api/types";
import { DynamicRepeater } from "@/components/DynamicRepeater/DynamicRepeater";
import { useTranslation } from "@/translations";

import type { SurveyRespondFormValues } from "..";
import { SurveyRespondQuestionCardDynamicRepeaterItem } from "./SurveyRespondQuestionCardDynamicRepeaterItem";
import { SurveyRespondQuestionCardDynamicRepeaterItemOverlay } from "./SurveyRespondQuestionCardDynamicRepeaterItemOverlay";

interface SurveyRespondFormRankingInputProps {
  questionId: string;
  offeredAnswers: OfferedAnswerDto[];
}

export function SurveyRespondFormRankingInput({
  questionId,
  offeredAnswers,
}: SurveyRespondFormRankingInputProps): React.ReactNode {
  const form = useFormContext<SurveyRespondFormValues>();
  const { t } = useTranslation();

  return (
    <div className="flex w-full flex-col gap-2">
      <span className="text-caption text-grey-700">{t("page.survey-respond.form.label.ranking-question")}</span>
      <Controller
        control={form.control}
        name={`${questionId}.ranking`}
        render={({ field: { onChange, value } }) => {
          const order: string[] = [];
          value?.forEach((item) => {
            order[item.sort] = item.answerId;
          });

          return (
            <SurveyRespondQuestionRankingInput
              options={offeredAnswers.map((offeredAnswer: OfferedAnswerDto) => ({
                title: offeredAnswer.text,
                description: offeredAnswer.subtitle,
                value: offeredAnswer.id,
              }))}
              order={order}
              onChange={(newOrder) => {
                const newValue = newOrder.map(
                  (item, index) =>
                    ({
                      answerId: item,
                      sort: index,
                    }) satisfies RankingAnswerItem,
                );

                onChange(newValue);
              }}
            />
          );
        }}
      />
    </div>
  );
}

type SurveyRespondQuestionRankingInputProps = {
  options: {
    title: string;
    description?: string;
    value: string;
  }[];
  order: string[];
  onChange: (order: string[]) => void;
};

function SurveyRespondQuestionRankingInput({
  options,
  order,
  onChange,
}: SurveyRespondQuestionRankingInputProps): React.ReactNode {
  const [dragItemId, setDragItemId] = useState<number | null>(null);

  function handleDragStart(event: DragStartEvent) {
    setDragItemId(event.active.data.current?.sortable.index);
  }

  function handleDragEnd(event: DragEndEvent) {
    setDragItemId(null);

    const { active, over } = event;

    if (over && active.id !== over.id) {
      const newOrder = [...order];

      const activeIndex = newOrder.indexOf(active.id as string);
      const overIndex = newOrder.indexOf(over.id as string);

      if (activeIndex === -1 || overIndex === -1) {
        return;
      }

      newOrder[activeIndex] = over.id as string;
      newOrder[overIndex] = active.id as string;

      onChange(newOrder);
    }
  }

  const sortedOptions = [...options].sort((a, b) => {
    const aIndex = order.indexOf(a.value);
    const bIndex = order.indexOf(b.value);

    return aIndex - bIndex;
  });

  const handleMoveItem = (id: string, direction: "up" | "down") => {
    const currentIndex = order.indexOf(id);
    const newOrder = [...order];

    if (direction === "up" && currentIndex > 0) {
      newOrder[currentIndex] = newOrder[currentIndex - 1];
      newOrder[currentIndex - 1] = id;
    } else if (direction === "down" && currentIndex < order.length - 1) {
      newOrder[currentIndex] = newOrder[currentIndex + 1];
      newOrder[currentIndex + 1] = id;
    }

    onChange(newOrder);
  };

  return (
    <div className="grid w-full grid-cols-[auto_1fr]">
      <div className="grid grid-cols-1">
        {options.map((option, index) => (
          <span key={`${option.value}-index`} className="flex w-8 justify-center self-center text-headline3">
            {index + 1}
          </span>
        ))}
      </div>
      <div className="flex flex-col gap-2">
        <DynamicRepeater onDragStart={handleDragStart} onDragEnd={handleDragEnd} itemIds={order}>
          {sortedOptions.map((option) => (
            <SurveyRespondQuestionCardDynamicRepeaterItem
              title={option.title}
              description={option.description}
              key={option.value}
              id={option.value}
              canMoveDown={order.indexOf(option.value) < order.length - 1}
              canMoveUp={order.indexOf(option.value) > 0}
              onMove={(direction) => handleMoveItem(option.value, direction)}
            />
          ))}
          {dragItemId !== null && (
            <SurveyRespondQuestionCardDynamicRepeaterItemOverlay
              title={sortedOptions[dragItemId]?.title}
              description={sortedOptions[dragItemId]?.description}
            />
          )}
        </DynamicRepeater>
      </div>
    </div>
  );
}
